import { RefObject } from "react"
import { DateTime } from "luxon"
import FullCalendar from "@fullcalendar/react"
import daLocale from "@fullcalendar/core/locales/da"

import type { DateTimeFactory } from "../../../../../../modules/localization/useDateTime"
import { AppLocalization } from "../../../../../../modules/localization/appLocalization"
import { getRoundedScrollTime } from "./helpers"

function roundDownToHour(dt: DateTime) {
    return dt.set({ minute: 0, second: 0, millisecond: 0 })
}

type DurationInput = { days?: number; hours?: number; minutes?: number; seconds?: number }

function centerDurationFor(anchorDay: DateTime): DurationInput {
    const now = DateTime.now()
    if (anchorDay.hasSame(now, "day")) {
        const h = roundDownToHour(now).hour
        return { days: 1, hours: h } // day #2, hour = current (rounded down)
    }
    return { days: 1, hours: 8 } // day 2, 8:00 a.m.
}

function computeThreeDayWindowAndCenterFromNewStart(newStart: DateTime) {
    const anchorDay = newStart.plus({ days: 1 }) // average day
    const scrollDuration = centerDurationFor(anchorDay)
    return { newStart, scrollDuration }
}

function nextWindowFromCurrentStart(currentStart: DateTime) {
    const newStart = currentStart.plus({ days: 1 })
    return computeThreeDayWindowAndCenterFromNewStart(newStart)
}

function prevWindowFromCurrentStart(currentStart: DateTime) {
    const newStart = currentStart.minus({ days: 1 })
    return computeThreeDayWindowAndCenterFromNewStart(newStart)
}

export const getFullCalendarConfig = (
    isDayGrid: boolean,
    l10n: AppLocalization,
    calendarRef: RefObject<FullCalendar | null>,
    dateTimeFactory: DateTimeFactory,
    locale?: string,
) => {
    // Map app locale to FullCalendar locale
    const fullCalendarLocale = locale?.startsWith("da") ? daLocale : undefined

    return {
        // initialView: isDayGrid ? "resourceTimelineOneDay" : "resourceTimelineWeek",
        initialView: isDayGrid ? "threeDayScroll" : "resourceTimelineWeek",
        locale: fullCalendarLocale,
        headerToolbar: {
            left: undefined,
            center: undefined,
            // right: isDayGrid ? "prev,today,next,goToDate" : "prev,currentWeek,next",
            right: isDayGrid ? "prevDay,goToToday,nextDay,goToDate" : "prev,currentWeek,next",
        },
        customButtons: {
            goToToday: {
                text: l10n.getString("create-order-calendar-today-button"),
                hint: l10n.getString("create-order-calendar-today-button-hint"),
                click: () => {
                    const api = calendarRef.current?.getApi()
                    if (!api) return

                    api.gotoDate(dateTimeFactory.now().minus({ days: 1 }).toJSDate())
                    api.scrollToTime(getRoundedScrollTime(dateTimeFactory))
                },
            },
            nextDay: {
                text: ">",
                hint: l10n.getString("create-order-calendar-next-day-button"),
                click: () => {
                    const api = calendarRef.current?.getApi()
                    if (!api) return

                    const currentStart = dateTimeFactory.fromJSDate(api.view.currentStart)
                    const { newStart, scrollDuration } = nextWindowFromCurrentStart(currentStart)

                    api.gotoDate(newStart.toJSDate())
                    api.scrollToTime(scrollDuration)
                },
            },
            prevDay: {
                text: "<",
                hint: l10n.getString("create-order-calendar-prev-day-button"),
                click: () => {
                    const api = calendarRef.current?.getApi()
                    if (!api) return

                    const currentStart = dateTimeFactory.fromJSDate(api.view.currentStart)
                    const { newStart, scrollDuration } = prevWindowFromCurrentStart(currentStart)

                    api.gotoDate(newStart.toJSDate())
                    api.scrollToTime(scrollDuration)
                },
            },
            currentWeek: {
                text: l10n.getString("create-order-calendar-week-button"),
                click: () => {
                    if (calendarRef?.current) {
                        const calendarApi = calendarRef.current.getApi()
                        const startOfWeek = DateTime.now().startOf("week")
                        calendarApi.gotoDate(startOfWeek.toISODate())
                    }
                },
            },
            goToDate: {
                text: l10n.getString("create-order-calendar-go-to-date-button"),
                hint: l10n.getString("create-order-calendar-go-to-date-button-hint"),
                click: () => {
                    const event = new CustomEvent("open-calendar-date-picker")
                    window.dispatchEvent(event)
                },
            },
        },
        views: {
            // resourceTimelineOneDay: {
            //     type: "resourceTimeline",
            //     duration: { days: 1 },
            //     slotLabelInterval: { hours: 1 },
            //     slotLabelFormat: [
            //         { weekday: "long" as "long", day: "2-digit" as "2-digit", month: "numeric" as "numeric" },
            //         { hour: "numeric" as "numeric", meridiem: "short" as "short" },
            //     ],
            //     resourceAreaWidth: 300,
            // },
            threeDayScroll: {
                type: "resourceTimeline",
                duration: { days: 3 },
                dateIncrement: { days: 1 },
                slotLabelInterval: { hours: 1 },
                slotLabelFormat: [
                    { weekday: "long" as "long", day: "2-digit" as "2-digit", month: "numeric" as "numeric" },
                    { hour: "numeric" as "numeric", meridiem: "short" as "short" },
                ],
                resourceAreaWidth: 300,
            },

            resourceTimelineWeek: {
                slotLabelFormat: [
                    { weekday: "short" as "short", day: "2-digit" as "2-digit", month: "numeric" as "numeric" },
                ],
                slotLabelInterval: { days: 1 },
                resourceAreaWidth: 300,
            },
        },
    }
}
